<main class="legal-page">

  <section class="legal-hero">
    <div class="legal-hero-content">
      <span class="legal-eyebrow">McTemplate</span>

      <h1>Terms & Privacy Policy</h1>

      <p>
        Information about how this website may be used,
        what information it collects, and how that information is handled.
      </p>

      <span class="legal-updated">
        Last updated: September 7, 2026
      </span>
    </div>
  </section>

  <section class="legal-content">

    <div class="legal-intro">
      <p>
        McTemplate is a website platform developed and maintained by
        Gav's Nexus for use by independently owned and operated
        McDonald's franchise organizations.
      </p>

      <p>
        The specific franchise organization operating a deployed version
        of this website is responsible for the business information,
        locations, employees, community content, donation information,
        links, and other material published through that website.
      </p>
    </div>

    <!-- TERMS -->
    <section class="legal-section">
      <h2>Terms of Use</h2>

      <h3>1. Use of This Website</h3>

      <p>
        This website is provided for informational and business purposes,
        including displaying restaurant locations, operating hours,
        community information, staff information, employment-related
        resources, donation request forms, and links to third-party
        services.
      </p>

      <p>
        By using this website, you agree not to misuse the website,
        interfere with its operation, attempt to gain unauthorized access
        to administrative systems, or use the website for unlawful
        purposes.
      </p>

      <h3>2. Restaurant and Franchise Information</h3>

      <p>
        Restaurant locations using McTemplate may be independently owned
        and operated McDonald's franchises.
      </p>

      <p>
        Individual franchise organizations are responsible for the
        accuracy of information displayed on their website, including
        restaurant hours, location information, community announcements,
        employment information, staff profiles, donation information,
        and other locally managed content.
      </p>

      <h3>3. McDonald's Trademarks</h3>

      <p>
        McDonald's names, logos, trademarks, trade dress, and related
        brand assets are the property of McDonald's Corporation and/or
        its respective affiliates and licensors.
      </p>

      <p>
        Gav's Nexus does not claim ownership of McDonald's trademarks or
        brand assets. Their presence within McTemplate is intended solely
        for websites operated for authorized McDonald's franchise
        organizations.
      </p>

      <h3>4. McTemplate Ownership</h3>

      <p>
        The McTemplate website platform, including its underlying
        application architecture, source code, custom components,
        administrative systems, layout, and original functionality,
        is developed and owned by Gav's Nexus unless otherwise stated.
      </p>

      <p>
        Content uploaded or entered by a franchise organization remains
        the responsibility of that organization and its authorized users.
      </p>

      <h3>5. Administrative Access</h3>

      <p>
        Certain portions of the website are restricted to authorized
        administrators, managers, or crew members.
      </p>

      <p>
        Users with administrative access are responsible for protecting
        their login credentials and must not share access with
        unauthorized individuals.
      </p>

      <p>
        Attempts to bypass authentication, access another user's account,
        modify restricted information without authorization, or interfere
        with website security are prohibited.
      </p>

      <h3>6. Third-Party Websites and Services</h3>

      <p>
        This website may contain links to third-party websites or
        services, including McDonald's ordering platforms, DoorDash,
        Uber Eats, YouTube, mapping services, social media platforms,
        or other external websites.
      </p>

      <p>
        These services operate independently and are governed by their
        own terms, privacy policies, and data practices. Neither the
        franchise organization nor Gav's Nexus controls the operation of
        those third-party services.
      </p>

      <h3>7. Availability and Accuracy</h3>

      <p>
        Reasonable efforts may be made to keep the website available and
        its information accurate. However, uninterrupted availability,
        error-free operation, or complete accuracy of all information
        cannot be guaranteed.
      </p>

      <p>
        Restaurant hours, events, promotions, staff information, and
        other content may change without notice.
      </p>

      <h3>8. Limitation of Liability</h3>

      <p>
        To the extent permitted by applicable law, Gav's Nexus and the
        organization operating this website are not responsible for
        indirect, incidental, consequential, or similar damages resulting
        from use of, inability to use, or reliance upon information
        provided through the website.
      </p>

      <h3>9. Changes to These Terms</h3>

      <p>
        These terms may be updated periodically to reflect changes to
        McTemplate, website functionality, applicable business practices,
        or legal requirements.
      </p>

      <p>
        The revision date displayed on this page indicates when these
        terms were most recently updated.
      </p>
    </section>

    <!-- PRIVACY -->
    <section class="legal-section">
      <h2>Privacy Policy</h2>

      <h3>1. Information You Provide</h3>

      <p>
        The website may collect information that you voluntarily submit
        through website forms.
      </p>

      <p>
        Depending on the features enabled by the franchise organization,
        this information may include:
      </p>

      <ul>
        <li>Name</li>
        <li>Email address</li>
        <li>Phone number</li>
        <li>Organization or business name</li>
        <li>Mailing address</li>
        <li>Event information</li>
        <li>Donation request information</li>
        <li>Employment or resume information</li>
        <li>Information included in a message or request</li>
      </ul>

      <h3>2. Donation Requests</h3>

      <p>
        Information submitted through the Donation Request form is used
        for the purpose of reviewing and responding to the request.
      </p>

      <p>
        Donation request information may be transmitted by email to the
        franchise organization responsible for reviewing donation
        requests.
      </p>

      <p>
        Submitting a request does not guarantee that a donation,
        sponsorship, or other request will be approved.
      </p>

      <h3>3. Employment and Resume Information</h3>

      <p>
        If employment or resume submission functionality is enabled,
        information submitted through those features is used for
        recruiting, employment, or applicant communication purposes.
      </p>

      <p>
        Employment decisions are made by the independently owned and
        operated franchise organization receiving the information unless
        otherwise stated.
      </p>

      <h3>4. Cookies and Authentication</h3>

      <p>
        McTemplate may use cookies or similar browser technologies
        necessary for website functionality and secure authentication.
      </p>

      <p>
        Authentication cookies may be used to maintain login sessions for
        authorized administrators, managers, and crew members.
      </p>

      <p>
        These cookies help protect restricted website functionality and
        identify whether an authorized user is currently signed in.
      </p>

      <h3>5. Technical Information</h3>

      <p>
        Standard technical information may be generated when visitors
        access the website, including information such as browser type,
        device information, request timestamps, IP address, error logs,
        and server activity.
      </p>

      <p>
        This information may be used for security, troubleshooting,
        performance monitoring, fraud prevention, and maintaining the
        website.
      </p>

      <h3>6. Analytics</h3>

      <p>
        A deployed McTemplate website may use website analytics to better
        understand how visitors interact with pages and website features.
      </p>

      <p>
        Analytics information may include page views, general traffic
        information, referral information, and interactions with website
        features.
      </p>

      <h3>7. Embedded and Third-Party Content</h3>

      <p>
        Pages may contain embedded or linked content from third-party
        providers such as YouTube, mapping providers, ordering platforms,
        delivery services, or social networks.
      </p>

      <p>
        When you interact with these services, the third-party provider
        may collect information according to its own privacy policy.
      </p>

      <h3>8. How Information Is Used</h3>

      <p>
        Information collected through this website may be used to:
      </p>

      <ul>
        <li>Respond to questions and requests</li>
        <li>Review donation requests</li>
        <li>Process employment-related submissions</li>
        <li>Operate and secure the website</li>
        <li>Maintain authorized administrative access</li>
        <li>Troubleshoot technical problems</li>
        <li>Improve website functionality and usability</li>
        <li>Prevent misuse or unauthorized access</li>
      </ul>

      <h3>9. Selling Personal Information</h3>

      <p>
        McTemplate is not designed to sell personal information submitted
        through the website.
      </p>

      <p>
        Information submitted through franchise-specific forms is
        intended to be used for the business purpose for which it was
        provided.
      </p>

      <h3>10. Information Sharing</h3>

      <p>
        Information may be shared with the franchise organization
        responsible for operating the website and with service providers
        reasonably necessary to operate website infrastructure,
        communications, hosting, security, or related functionality.
      </p>

      <p>
        Information may also be disclosed when reasonably necessary to
        comply with applicable law, enforce website security, or protect
        the rights and safety of users or others.
      </p>

      <h3>11. Data Security</h3>

      <p>
        Reasonable technical and organizational measures are used to
        protect website systems and information from unauthorized access,
        alteration, or disclosure.
      </p>

      <p>
        However, no internet-based service, email transmission, or
        electronic storage system can be guaranteed to be completely
        secure.
      </p>

      <h3>12. Children's Privacy</h3>

      <p>
        This website is not intended to knowingly collect personal
        information from children under 13 without appropriate
        authorization.
      </p>

      <h3>13. Privacy Requests</h3>

      <p>
        Questions regarding information submitted through a deployed
        franchise website should generally be directed to the franchise
        organization operating that website.
      </p>

      <p>
        Questions specifically regarding the McTemplate platform or
        Gav's Nexus may be directed to:
      </p>

      <a
        class="legal-email"
        href="mailto:callmehgav@gmail.com?cc=ganderson96@live.com&subject=McTemplate%20Privacy%20Inquiry">
        Contact Gav's Nexus
      </a>

      <h3>14. Changes to This Privacy Policy</h3>

      <p>
        This Privacy Policy may be updated as McTemplate functionality,
        data practices, or applicable requirements change.
      </p>

      <p>
        Any updated version will be posted on this page with a revised
        effective date.
      </p>
    </section>

    <section class="legal-footer-note">
      <h2>Questions?</h2>

      <p>
        For questions concerning this website, contact the franchise
        organization operating it. For questions regarding the McTemplate
        platform itself, contact Gav's Nexus.
      </p>

      <a
        href="mailto:callmehgav@gmail.com?cc=ganderson96@live.com&subject=McTemplate%20Website%20Inquiry"
        class="legal-contact-btn">
        Contact Gav's Nexus
      </a>
    </section>

  </section>

</main>